import { ref, watch, type Ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';

import {
  booleanParam,
  listParam,
  parseQueryParams,
  syncQueryParams,
  textParam,
} from './QueryParams';

export interface QueryParam<T> {
  parse: (rawValue: unknown) => T;
  serialize: (value: T) => string | undefined;
}

export interface ExtraParam {
  param: QueryParam<boolean>;
  ref?: Ref<boolean>;
}

export interface UseSeriesChartQueryOptions {
  allSeries: Readonly<Ref<string[]>>;
  extraParams?: Record<string, ExtraParam>;
}

export interface SeriesChart {
  hideSeries: (series: string) => void;
}

export interface UseSeriesChartQueryReturn {
  period: Ref<string>;
  chartType: Ref<string>;
  stacked: Ref<boolean>;
  unique: Ref<boolean>;
  extras: Record<string, Ref<boolean>>;
  activeSeries: Ref<string[]>;
  updateQueryString: () => void;
  updateActiveSeries: (series: string[]) => void;
  resetActiveSeries: () => void;
  hideInactiveSeries: (chart: SeriesChart) => void;
}

// An extra param can bring its own ref when it must exist before this runs,
// for example because `allSeries` depends on it.
export function useSeriesChartQuery({
  allSeries,
  extraParams = {},
}: UseSeriesChartQueryOptions): UseSeriesChartQueryReturn {
  const route = useRoute();
  const router = useRouter();

  const queryParams = () => ({
    period: textParam('month'),
    chartType: textParam('bar'),
    stacked: booleanParam(true),
    unique: booleanParam(true),
    ...Object.fromEntries(Object.entries(extraParams).map(([key, { param }]) => [key, param])),
    series: listParam({ allowed: allSeries.value, fallback: allSeries.value }),
  });

  const initialValues = parseQueryParams(route.query, queryParams());

  const period = ref<string>(initialValues.period);
  const chartType = ref<string>(initialValues.chartType);
  const stacked = ref<boolean>(initialValues.stacked);
  const unique = ref<boolean>(initialValues.unique);
  const extras: Record<string, Ref<boolean>> = Object.fromEntries(
    Object.entries(extraParams).map(([key, extra]) => [key, extra.ref ?? ref<boolean>(initialValues[key])]),
  );
  const activeSeries = ref<string[]>(initialValues.series);

  const updateQueryString = () => {
    syncQueryParams(router, queryParams(), {
      period: period.value,
      chartType: chartType.value,
      stacked: stacked.value,
      unique: unique.value,
      ...Object.fromEntries(Object.entries(extras).map(([key, extra]) => [key, extra.value])),
      series: activeSeries.value,
    });
  };

  const updateActiveSeries = (series: string[]) => {
    activeSeries.value = series;
    updateQueryString();
  };

  const resetActiveSeries = () => {
    activeSeries.value = queryParams().series.parse(route.query.series);
  };

  const hideInactiveSeries = (chart: SeriesChart) => {
    allSeries.value
      .filter(series => !activeSeries.value.includes(series))
      .forEach((series) => {
        chart.hideSeries(series);
      });
  };

  watch([chartType, stacked], updateQueryString);

  return {
    period,
    chartType,
    stacked,
    unique,
    extras,
    activeSeries,
    updateQueryString,
    updateActiveSeries,
    resetActiveSeries,
    hideInactiveSeries,
  };
}
